// Lesson 1.1, Your AI, your job. The free sample lesson.
//
// Approved by Leena 2026-10-09 and converted mechanically from the draft
// (artifact 5ML4JJh9mGyaNJBJxaQym3, v2). The words here are the approved words.

export default {
  key: '1-1',
  title: 'Your AI, your job',
  minutes: 15,
  status: 'ready',
  outcome: 'A quick primer on the three tools, the admin tasks you want gone, and your starting time estimate.',

  why: 'You have been told for two years that AI will change your job. Nobody has shown you what that means for your actual Tuesday. This course is about your Tuesday. By the end of this first lesson you will have used one of these tools on something real and seen whether it was worth the fifteen minutes.',

  blocks: [
    {
      type: 'concept',
      heading: 'How this course works',
      body: [
        { type: 'p', text: 'Six lessons, fifteen to twenty minutes each. Read the lesson, then do the one exercise, which always uses your own real work rather than a made-up example. Five things worth knowing before you start.' },

        { type: 'sub', text: 'The prompt cards are the thing you keep' },
        { type: 'p', text: 'Every lesson ends with a prompt card: a ready-made instruction you can copy and use straight away. Press **Save to my library** and it goes into your prompt library on the course page, which stays yours after the course finishes. By the end you will have a handful that fit your actual job. That library, not the lessons, is what you are really here for.' },

        { type: 'sub', text: 'How to use one, if you have never done this' },
        { type: 'p', text: 'A prompt is just text you send to the AI, so using a card takes three steps.' },
        { type: 'steps', items: [
          '**Copy the card’s text** and paste it into the chat box of your AI tool.',
          '**Underneath it, give it the thing to work on.** Paste the email or the document straight in, or use the paperclip or plus button to attach a file if your tool has one.',
          '**Send both together** as one message. The instruction and the material need to arrive at the same time.',
        ] },
        { type: 'p', text: 'That is it. It feels clumsy the first time and takes about ten seconds once you have done it twice. Later in the course we show you how to set your tool up so you stop pasting the same things over and over.' },

        { type: 'sub', text: 'Try talking instead of typing' },
        { type: 'p', text: 'Nearly every one of these tools has a microphone button. Use it. When people type they get terse, and terse means the tool has less to work with. When people talk they explain properly, almost without noticing, and the answers come back better for it. It is usually faster than typing too.' },
        { type: 'p', text: 'One caveat so nobody is surprised: speaking uses slightly more of your plan’s allowance than typing the same thing. On a company account that is almost never worth thinking about. On a tight personal limit, it is worth knowing.' },

        { type: 'sub', text: 'The quick checks are optional' },
        { type: 'p', text: 'Each lesson ends with two or three questions. They are there if you want to confirm something landed, and they are genuinely optional. Skip them and nothing is held back, nothing is marked incomplete, and no lesson stays locked. Plenty of people will skip them, and that is fine.' },

        { type: 'sub', text: 'Settings change. Ideas do not.' },
        { type: 'p', text: 'Where we give you a menu path, treat it as a strong hint rather than gospel. These products ship changes constantly and a menu can move in a month. The concepts in this course have been stable for years and will outlast any particular button. If a path does not match what you see, look for the nearest equivalent, and tell us in the box at the end of the lesson so we can fix it for the next person.' },
      ],
    },

    {
      type: 'concept',
      heading: 'Three tools, more alike than different',
      body: [
        { type: 'p', text: 'ChatGPT, Claude and Gemini are the three worth your time. People argue about which is best. For the work in this course the honest answer is that any of them will do, and the one you already have access to is the right one. Pick it and go deep rather than sampling all three.' },
        { type: 'p', text: 'What they are genuinely good at is language work: turning a mess into a draft, a long thing into a short thing, rough notes into something you would send. That covers a surprising amount of a working week.' },
        { type: 'p', text: 'What they are bad at is anything that needs to be true on its own: specific numbers, who said what, what a policy says, whether a link exists. They will answer confidently and be wrong. Lesson 1.2 is entirely about handling that.' },
        { type: 'rule', text: 'Good at shaping what you give it. Bad at knowing things you did not give it.' },
        { type: 'callout', tone: 'info', text: 'A note on the hype. You do not need to become a prompt engineer, and you do not need a new tool every month. Five prompts that fit your actual job will give you back more time than a hundred clever ones you never use again. That is what you are building here.' },
      ],
    },

    {
      type: 'exercise',
      heading: 'The admin you would hand over tomorrow',
      intro: 'Before you learn any technique, write down what you would actually want help with. Not what AI is supposedly for. What eats your week.',
      steps: [
        { id: 'list_admin', text: 'Think about last week. List three to five jobs you would hand to a competent assistant tomorrow if you had one. Writing up meeting notes. Chasing a quote. Turning a call into a follow-up email. Rewriting the same explanation for the fifth time.' },
        { id: 'add_to_list', text: 'Add each one to the list on the course page. You will come back to it in the final lesson and tick off what actually went away.' },
        { id: 'time_estimate', text: 'Answer the time question on the course page: roughly how many hours a week do you spend on admin like email and documents? A rough number is fine. It is the only way you will know at the end whether any of this worked.' },
      ],
    },

    {
      type: 'concept',
      body: [
        { type: 'callout', tone: 'info', text: 'One honest warning. If your list is all judgement calls and relationships, AI will not take much off your plate, and you should know that now rather than after five lessons. Most people’s lists are at least half writing and shaping, which is exactly where this pays off.' },
      ],
    },

    {
      type: 'quickcheck',
      questions: [
        {
          q: 'Which of these is the kind of job these tools are actually good at?',
          options: ['Telling you last quarter’s exact revenue', 'Turning your rough notes into a clean client update', 'Deciding whether to hire someone'],
          answer: 1,
          because: 'Shaping something you supply is the sweet spot. It does not know your numbers, and the hiring call is yours.',
        },
        {
          q: 'You are picking a tool to learn. What is the best reason to choose one?',
          options: ['It scored highest on a benchmark', 'Your company already pays for it', 'It is the newest'],
          answer: 1,
          because: 'Access beats benchmarks. The gap between these tools is far smaller than the gap between using one properly and not.',
        },
      ],
    },

    {
      type: 'promptcard',
      title: 'Explain it like I am busy',
      body: 'Explain this in plain language for someone who has about two minutes and needs to make a decision. Lead with what it means for me. Skip the background unless I cannot understand the point without it. Then list anything I would be wrong about if I only read your summary.',
      howTo: 'How to use this one, step by step. Find the next long email or document that lands on you. Copy the prompt text above and paste it into your AI tool’s chat box. Then, on the line below it, paste the whole email or document in, or attach the file with the paperclip button. Send it as one message. If you would rather talk than type, press the microphone and read the prompt out, then paste the document underneath.',
      note: 'It is the one prompt most people keep from this course.',
    },

    {
      type: 'takeaway',
      text: 'These tools are good at shaping what you give them and bad at knowing what you did not. Start there and you will not be disappointed.',
    },
  ],

  videoNote: 'Leena introduces the course.',
};
